import { PersonRole } from '@/enums/PersonRole'
import { useAuthStore } from '@/stores/auth_store'
import type { RouteRecordRaw } from 'vue-router'

export enum RouteName {
  EducationContractRenewalList = 'EducationContractRenewalList',
  EducationContractWizard = 'EducationContractWizard',
}

const routes: RouteRecordRaw[] = [
  {
    path: '/m/',
    component: () => import('@/layouts/ManagerLayout.vue'),
    meta: { condition: ($auth: ReturnType<typeof useAuthStore>) => $auth.person?.employee },
    children: [
      {
        path: '',
        component: () => import('@/pages/Manager/DashboardPage.vue'),
        props: true,
      },
      {
        path: 'people/:personId',
        component: () => import('@/pages/Manager/Person/PersonPage.vue'),
        props: true,
        name: 'manager.person',
      },
      {
        path: 'aa',
        component: () => import('@/pages/AdmissionApplication/AdmissionApplicationListPage.vue'),
        props: true,
        name: 'manager.admission-application.index',
      },
      {
        path: 'aa/:id',
        component: () => import('@/pages/AdmissionApplication/AdmissionApplicationPage.vue'),
        props: true,
        name: 'manager.admission-application.show',
      },
      {
        path: 'ad',
        component: () =>
          import('@/pages/AdmissionDiagnostic/AdmissionDiagnosticListManagerPage.vue'),
        props: true,
        name: 'manager.admission-diagnostic.index',
      },
      {
        path: 'en/:id',
        component: () => import('@/pages/Manager/Enrollment/EnrollmentPage.vue'),
        props: true,
        name: 'manager.enrollment.view',
      },
      {
        path: 'en',
        component: () => import('@/pages/Manager/Enrollment/EnrollmentListPage.vue'),
        name: 'manager.enrollment.index',
      },
      {
        path: 'ec/:id',
        component: () => import('@/pages/EducationContract/EducationContractPage.vue'),
        props: true,
        name: 'manager.education-contract',
      },
      {
        path: 'ec',
        component: () => import('@/pages/EducationContract/EducationContractListPage.vue'),
        name: 'manager.education-contract.index',
      },
      {
        path: 'ads',
        component: () =>
          import('@/pages/Manager/AdmissionDiagnosticSlot/AdmissionDiagnosticSlotListPage.vue'),
        props: true,
        name: 'manager.admission-diagnostic-slot',
      },
      {
        path: 'so',
        component: () => import('@/pages/SMART/Manager/SmartOfferListPage.vue'),
        name: 'manager.smart-offer.index',
      },
      {
        path: 'so/:id',
        component: () => import('@/pages/SMART/Manager/SmartOfferEditPage.vue'),
        props: true,
        name: 'manager.smart-offer.edit',
      },
      {
        path: 'ss',
        component: () => import('@/pages/SMART/Manager/SmartSubscriptionListPage.vue'),
        name: 'manager.smart-subscription.index',
      },
      {
        path: 'ss/new',
        component: () => import('@/pages/SMART/Manager/SmartSubscriptionCreatePage.vue'),
        name: 'manager.smart-subscription.create',
      },
      {
        path: 'ss/:id',
        component: () => import('@/pages/SMART/Manager/SmartSubscriptionEditPage.vue'),
        name: 'manager.smart-subscription.edit',
        props: true,
      },
      {
        path: 'wf',
        component: () => import('@/pages/Manager/WF/WFShowPage.vue'),
        props: true,
        name: 'manager.wf',
      },
      {
        path: 'tech_icons',
        component: () => import('@/pages/Manager/Technical/IconsList.vue'),
        props: true,
        name: 'manager.icons',
      },
      {
        path: 'food',
        component: () => import('@/pages/Manager/FoodPage.vue'),
        name: 'manager.food',
      },
      {
        path: 'pep-campaign',
        component: () => import('@/pages/PEP/Campaign/PepCampaignListPage.vue'),
        name: 'manager.pep-campaign.index',
      },
      {
        path: 'pep-campaign/:id',
        component: () => import('@/pages/PEP/Campaign/PepCampaignEditPage.vue'),
        name: 'manager.pep-campaign.edit',
        props: true,
      },
      {
        path: 'pep-application',
        component: () => import('@/pages/PEP/Application/PepApplicationListManagerPage.vue'),
        name: 'manager.pep-application.index',
      },
      {
        path: 'pep-application/:id',
        component: () => import('@/pages/PEP/Application/PepApplicationManagerPage.vue'),
        name: 'manager.pep-application.edit',
        props: (route) => ({
          id: route.params.id,
          personId: route.query?.pid,
          campaignId: route.query?.cid,
        }),
      },
      {
        path: 'pep-application-groups',
        component: () => import('@/pages/PEP/Application/PepApplicationGroupsManagerPage.vue'),
        name: 'manager.pep-application-groups.index',
        props: true,
      },
      {
        path: 'mailing',
        component: () => import('@/pages/Mailing/MailingListPage.vue'),
        name: 'manager.mailing.index',
      },
      {
        path: 'mailing/:id',
        component: () => import('@/pages/Mailing/MailingEditPage.vue'),
        name: 'manager.mailing.edit',
        props: true,
      },
      /*{ path: 'chat', component: () => import('@/pages/Chat/ChatPage.vue') },*/
      /*{
        path: 'student',
        component: () => import('@/pages/Student/StudentPage.vue')
      }*/
      {
        path: 'se',
        component: () => import('@/pages/SkolcaEvent/SkolcaEventListPage.vue'),
        name: 'manager.skolca-event.index',
      },
      {
        path: 'se/:id',
        component: () => import('@/pages/SkolcaEvent/SkolcaEventEditPage.vue'),
        name: 'manager.skolca-event.edit',
        props: true,
      },
      {
        path: 'tc',
        component: () =>
          import('@/pages/Manager/TeacherConsultation/TeacherConsultationListPage.vue'),
        name: 'manager.teacher-consultation.index',
      },
      {
        path: 'solr',
        component: () =>
          import('@/pages/Manager/StudentOnlineLessonRequest/StudentOnlineLessonRequestListPage.vue'),
        name: 'manager.student-online-lesson-request.index',
      },
    ],
  },
  {
    path: '/pay',
    component: () => import('@/layouts/SimpleLayout.vue'),
    children: [
      {
        path: '',
        component: () => import('@/pages/Payment/PaymentRedirectPage.vue'),
      },
      {
        path: '/pay-return',
        component: () => import('@/pages/Payment/PaymentReturnPage.vue'),
      },
    ],
  },
  {
    path: '/',
    component: () => import('@/layouts/ClientLayout.vue'),
    children: [
      {
        path: '_',
        component: () => import('@/pages/Dashboard/DashboardPage.vue'),
      },
      {
        path: 'stats',
        component: () => import('@/pages/Stats/StatsPage.vue'),
      },
      {
        path: 'saip',
        component: () => import('@/pages/Dashboard/SkolcaAppInstructionPage.vue'),
      },
      {
        path: '',
        component: () => import('@/pages/Dashboard/DashboardPage.vue'),
      },
      {
        path: '/cn',
        component: () => import('@/pages/CabinetNew/CabinetRootPage.vue'),
        props: (route) => ({
          person_id: route.query.pid,
        }),
      },
      {
        path: '/cn/pd',
        component: () => import('@/pages/CabinetNew/CabinetPersonDataPage.vue'),
        props: (route) => ({
          person_id: route.query.pid,
        }),
      },
      {
        path: '/cn/l',
        component: () => import('@/pages/CabinetNew/CabinetLocationLogPage.vue'),
        props: (route) => ({
          person_id: route.query.pid,
        }),
      },
      {
        path: '/c',
        component: () => import('@/layouts/CabinetLayout.vue'),
        children: [
          {
            path: 'profile',
            component: () => import('@/pages/Cabinet/ProfilePage.vue'),
          },
          {
            path: 'acc',
            component: () => import('@/pages/Cabinet/AccountsPage.vue'),
          },
          {
            path: 'family',
            component: () => import('@/pages/Cabinet/FamilyPage.vue'),
          },
          {
            path: 'docs',
            component: () => import('@/pages/Cabinet/CabinetDocumentsPage.vue'),
          },
          {
            path: 'pay',
            component: () => import('@/pages/Cabinet/CabinetPaymentsPage.vue'),
          },
        ],
      },
      {
        path: 'aa/:admissionApplicationId/reserve',
        component: () =>
          import('@/pages/AdmissionApplicationWizard/AdmissionApplicationReserveListPage.vue'),
        props: true,
      },
      {
        path: 'aa/:admissionApplicationId',
        component: () =>
          import('@/pages/AdmissionApplicationWizard/AdmissionApplicationWizardPage.vue'),
        props: true,
        name: 'admission-application.wizard',
      },
      {
        path: 'ad/:id',
        component: () => import('@/pages/AdmissionDiagnostic/AdmissionDiagnosticClientPage.vue'),
        props: true,
      },
      {
        path: 'ad',
        component: () =>
          import('@/pages/AdmissionDiagnostic/AdmissionDiagnosticListClientPage.vue'),
      },
      {
        path: 'aa',
        component: () =>
          import('@/pages/AdmissionApplicationWizard/AdmissionApplicationListClientPage.vue'),
      },
      {
        path: 'tcp',
        component: () => import('@/pages/Client/TeachersConsultParentPage.vue'),
      },
      {
        path: 'tct',
        component: () => import('@/pages/Client/TeachersConsultTeacherPage.vue'),
      },
      {
        path: 'solr',
        component: () =>
          import('@/pages/StudentOnlineLessonRequest/StudentsOnlineLessonsRequestsParentPage.vue'),
      },
      {
        path: 'tcs',
        component: () => import('@/pages/Client/TeachersConsultStudentsPage.vue'),
      },
      {
        path: 'chat/:id',
        component: () => import('@/pages/Chat/ChatPage.vue'),
        props: true,
        name: 'chat.view',
      },
      {
        path: 'chat',
        component: () => import('@/pages/Chat/ChatPage.vue'),
        props: true,
        name: 'chat.index',
      },
      {
        path: 'ss',
        component: () => import('@/pages/SMART/Client/SmartSubscriptionDashboardPage.vue'),
        name: 'client.smart-subscription.index',
      },
      {
        path: 'ss/:id',
        component: () => import('@/pages/SMART/Client/SmartSubscriptionWizardPage.vue'),
        name: 'client.smart-subscription.wizard',
      },
      {
        path: 'ece/:educationContractId',
        component: () => import('@/pages/EducationContractWizard/EducationContractWizardPage.vue'),
        name: RouteName.EducationContractWizard,
        props: (route) => ({
          educationContractId: route.params.educationContractId,
          personId: route.query.pid,
        }),
      },
      {
        path: 'ece',
        component: () =>
          import('@/pages/EducationContractWizard/EducationContractRenewalListPage.vue'),
        name: RouteName.EducationContractRenewalList,
      },
      {
        path: 'pepn',
        component: () => {
          const $auth = useAuthStore()
          if ($auth.hasRole(PersonRole.Student)) {
            return import('@/pages/PEP/Application/PepApplicationListStudentPage.vue')
          } else if ($auth.hasRole(PersonRole.ParentOfStudent)) {
            return import('@/pages/PEP/Application/PepApplicationListParentPage.vue')
          } else {
            return import('@/pages/ErrorNotFound.vue')
          }
        },
      },
      {
        path: 'pepn/:id',
        component: () => {
          const $auth = useAuthStore()
          if ($auth.hasRole(PersonRole.Student)) {
            return import('@/pages/PEP/Application/PepApplicationStudentPage.vue')
          } else if ($auth.hasRole(PersonRole.ParentOfStudent)) {
            return import('@/pages/PEP/Application/PepApplicationParentPage.vue')
          } else {
            return import('@/pages/ErrorNotFound.vue')
          }
        },
        props: (route) => ({
          id: route.params.id,
          campaign_id: route.query.campaign_id,
        }),
      },
      {
        path: 'pep',
        children: [
          {
            path: ':childrenPersonId([A-Za-z0-9]+)?',
            props: true,
            component: () => {
              const $auth = useAuthStore()
              if ($auth.hasRole(PersonRole.Student)) {
                return import('@/pages/PEP/Current/PepStudentPage.vue')
              } else if ($auth.hasRole(PersonRole.ParentOfStudent)) {
                return import('@/pages/PEP/Current/PepParentPage.vue')
              } else {
                return import('@/pages/ErrorNotFound.vue')
              }
            },
          },
          {
            path: ':childrenPersonId([A-Za-z0-9]+)?/:typeId([0-9]+)',
            component: () => import('@/pages/PEP/Current/PepStudentLessonsPage.vue'),
            props: true,
          },
          {
            path: ':childrenPersonId([A-Za-z0-9]+)?/:typeId([0-9]+)/:lessonId([0-9]+)',
            component: () => import('@/pages/PEP/Current/PepStudentLessonPage.vue'),
            props: (route) => ({
              lessonId: route.params.lessonId,
              childrenPersonId: route.params.childrenPersonId,
              startDate: route.query.s,
              endDate: route.query.e,
            }),
          },
        ],
      },

      {
        path: 'se',
        component: () => import('@/pages/SkolcaEvent/SkolcaEventShowcasePage.vue'),
        name: 'client.skolca-event.index',
      },
      {
        path: 'se/:id',
        component: () => import('@/pages/SkolcaEvent/SkolcaEventShowPage.vue'),
        name: 'client.skolca-event.show',
        props: true,
      },
    ],
  },
  {
    path: '/auth',
    redirect: '/auth/login',
  },
  {
    path: '/auth/',
    component: () => import('@/layouts/SimpleLayout.vue'),
    children: [
      {
        path: 'login',
        component: () => import('@/pages/Auth/LoginPage.vue'),
        props: true,
        name: 'auth.login',
      },
      {
        path: 'register',
        component: () => import('@/pages/Auth/RegistrationPage.vue'),
        props: true,
        name: 'auth.register',
      },
      {
        path: 'phone',
        component: () => import('@/pages/Auth/PhoneEmailRegistrationPage.vue'),
        props: { type: 'phone' },
        name: 'auth.phone_confirmation',
      },
      {
        path: 'email',
        component: () => import('@/pages/Auth/PhoneEmailRegistrationPage.vue'),
        props: { type: 'email' },
        name: 'auth.email_confirmation',
      },
    ],
  },
  {
    path: '/auth-ext',
    component: () => import('@/layouts/SimpleLayout.vue'),
    children: [
      {
        path: '',
        component: () => import('@/pages/Auth/AuthExternalApp.vue'),
        props: (route) => ({
          redirect_uri: route.query.redirect_uri,
        }),
        name: 'auth.external',
      },
    ],
  },
  {
    path: '/aqr',
    component: () => import('@/layouts/SimpleLayout.vue'),
    props: { showLogout: true },
    children: [
      {
        path: '',
        component: () => import('@/pages/Client/AssistantAuthPage.vue'),
        props: { type: 'auth', persistent: true },
      },
    ],
  },
  {
    path: '/:catchAll(.*)*',
    component: () => import('@/pages/ErrorNotFound.vue'),
  },
]

export default routes
